/**
 * The room's status, read and edited.
 *
 * A modal behind a header button rather than a band above the stream. A band
 * that never collapses taxes every glance at the room, and the room is the
 * product — but a modal hides staleness, which is why the button that opens
 * this carries the signal (see `buttonState`).
 *
 * Three sections, each editable on its own, because that is also how the server
 * writes them: two people editing different sections never collide.
 */
import { useEffect, useRef, useState } from 'react'
import type { CSSProperties } from 'react'
import { radius } from '../components/primitives/shape'
import type { RoomStatus, StatusSection } from '../api/types'
import { parseBody } from '../room/format'
import { HEADINGS, SECTION_ORDER, describeAge, inReadingOrder, statusLines } from './status'

const MAX_LENGTH = 1200

/** Inline `code` and @mentions, the same vocabulary the stream uses. */
function Line({ body }: { body: string }) {
  return (
    <>
      {parseBody(body).map((part, index) => (
        <span
          key={index}
          style={
            part.kind === 'mention'
              ? { color: 'var(--mention-agent)', fontWeight: 700 }
              : part.kind === 'code'
                ? { fontFamily: 'var(--font-machine)', fontSize: '.88em', color: '#fff' }
                : undefined
          }
        >
          {part.text}
        </span>
      ))}
    </>
  )
}

function Body({ body }: { body: string }) {
  const lines = statusLines(body)
  const items = lines.filter((line) => line.kind === 'item')
  return (
    <>
      {lines
        .filter((line) => line.kind === 'text')
        .map((line, index) => (
          <p key={`t${index}`} className="sp-said" style={{ margin: '0 0 6px', color: 'var(--text-body)', lineHeight: 'var(--line-body)' }}>
            <Line body={line.body} />
          </p>
        ))}
      {items.length > 0 && (
        <ul className="sp-said" style={{ margin: 0, paddingLeft: 16, color: 'var(--text-body)', lineHeight: 'var(--line-body)' }}>
          {items.map((line, index) => (
            <li key={`i${index}`} style={{ marginBottom: 4 }}>
              <Line body={line.body} />
            </li>
          ))}
        </ul>
      )}
    </>
  )
}

const headingStyle: CSSProperties = {
  margin: '0 0 6px',
  font: '400 var(--size-label)/1 var(--font-machine)',
  letterSpacing: '.15em',
  textTransform: 'uppercase',
  color: 'var(--text-dim)',
  display: 'flex',
  alignItems: 'baseline',
  gap: 9,
}

function Section({
  section,
  row,
  editing,
  draft,
  onEdit,
  onDraft,
  onSave,
  onCancel,
}: {
  section: StatusSection['section']
  row: StatusSection | undefined
  editing: boolean
  draft: string
  onEdit: () => void
  onDraft: (value: string) => void
  onSave: () => void
  onCancel: () => void
}) {
  const over = draft.length > MAX_LENGTH
  return (
    <div style={{ padding: '15px 0', borderTop: '1px solid var(--border)' }}>
      <h4 style={headingStyle}>
        {HEADINGS[section]}
        <span
          style={{
            marginLeft: 'auto',
            font: '400 var(--size-meta)/1 var(--font-machine)',
            letterSpacing: 0,
            textTransform: 'none',
            color: 'var(--text-faint)',
          }}
        >
          {row ? `${row.author} · ${describeAge(row.updated_at)}` : 'not set'}
        </span>
      </h4>

      {editing ? (
        <>
          <textarea
            aria-label={`${HEADINGS[section]} text`}
            value={draft}
            onChange={(event) => onDraft(event.target.value)}
            rows={4}
            style={{
              width: '100%',
              resize: 'vertical',
              background: 'transparent',
              border: '1px solid var(--accent-edge)',
              borderRadius: radius.sm,
              padding: '9px 11px',
              color: 'var(--text)',
              font: 'var(--size-body)/var(--line-body) var(--font-interface)',
              outline: 'none',
            }}
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 7 }}>
            <span style={{ font: `400 var(--size-meta)/1 var(--font-machine)`, color: over ? 'var(--deletions)' : 'var(--text-faint)' }}>
              {draft.length}/{MAX_LENGTH}
            </span>
            <span style={{ flex: 1 }} />
            <button onClick={onCancel} style={{ font: '400 12px var(--font-interface)', color: 'var(--text-dim)', cursor: 'pointer' }}>
              Cancel
            </button>
            <button
              onClick={onSave}
              disabled={over}
              style={{
                border: '1px solid var(--accent-edge)',
                background: 'var(--accent-wash)',
                color: over ? 'var(--text-faint)' : 'var(--accent-bright)',
                borderRadius: radius.action,
                padding: '5px 13px',
                font: '600 12px var(--font-interface)',
                cursor: over ? 'not-allowed' : 'pointer',
              }}
            >
              Save
            </button>
          </div>
        </>
      ) : (
        <div onDoubleClick={onEdit}>
          {row ? <Body body={row.body} /> : (
            <p style={{ margin: 0, color: 'var(--text-faint)', fontStyle: 'italic' }}>
              Nothing recorded.
            </p>
          )}
          <button
            onClick={onEdit}
            className="sp-tap"
            style={{
              marginTop: 7,
              font: '400 var(--size-meta)/1 var(--font-machine)',
              color: 'var(--text-meta)',
              border: '1px solid var(--border)',
              borderRadius: radius.xs,
              padding: '6px 10px',
              cursor: 'pointer',
            }}
          >
            Edit
          </button>
        </div>
      )}
    </div>
  )
}

export function StatusModal({
  status,
  roomName,
  onClose,
  onSave,
}: {
  status: RoomStatus | null
  roomName: string
  onClose: () => void
  onSave: (sections: { now?: string; blocked?: string; up_next?: string }) => Promise<void> | void
}) {
  const [editing, setEditing] = useState<StatusSection['section'] | null>(null)
  const [draft, setDraft] = useState('')
  const shell = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  const rows = inReadingOrder(status?.sections ?? [])
  const byName = new Map(rows.map((row) => [row.section, row]))

  function startEditing(section: StatusSection['section']) {
    setEditing(section)
    setDraft(byName.get(section)?.body ?? '')
  }

  async function save() {
    if (!editing) return
    await onSave({ [editing]: draft })
    setEditing(null)
  }

  return (
    <div
      role="dialog"
      aria-label="Room status"
      onMouseDown={(event) => {
        if (event.target === shell.current) onClose()
      }}
      ref={shell}
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 40,
        display: 'grid',
        placeItems: 'center',
        background: 'rgba(0,0,0,.62)',
      }}
    >
      <div
        style={{
          width: 'min(600px, calc(100% - 32px))',
          maxHeight: 'calc(100% - 56px)',
          // The modal covers the bottom bar, so the home indicator is its own
          // to keep clear of.
          marginBottom: 'var(--safe-bottom)',
          display: 'flex',
          flexDirection: 'column',
          background: 'var(--ground-raised)',
          border: '1px solid var(--border-strong)',
          borderRadius: radius.xl,
          boxShadow: 'var(--shadow-overlay)',
          overflow: 'hidden',
        }}
      >
        <header
          style={{
            padding: '17px 22px 13px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'baseline',
            gap: 11,
            flex: 'none',
          }}
        >
          <h2 style={{ margin: 0, font: '700 15px var(--font-interface)', letterSpacing: '-.01em' }}>Status</h2>
          <span style={{ font: '400 var(--size-meta)/1 var(--font-machine)', color: 'var(--text-meta)' }}>
            {roomName} · updated {describeAge(status?.updated_at ?? null)}
          </span>
          <button
            onClick={onClose}
            aria-label="Close"
            className="sp-tap"
            style={{ marginLeft: 'auto', flex: 'none', font: '400 12px var(--font-machine)', color: 'var(--text-faint)', cursor: 'pointer' }}
          >
            esc
          </button>
        </header>

        <div
          style={{
            overflow: 'auto', padding: '0 22px 14px',
            overscrollBehaviorY: 'contain', WebkitOverflowScrolling: 'touch',
          }}
        >
          {SECTION_ORDER.map((section) => (
            <Section
              key={section}
              section={section}
              row={byName.get(section)}
              editing={editing === section}
              draft={draft}
              onEdit={() => startEditing(section)}
              onDraft={setDraft}
              onSave={save}
              onCancel={() => setEditing(null)}
            />
          ))}
          <p
            style={{
              margin: '14px 0 0',
              font: '400 var(--size-meta)/1.5 var(--font-machine)',
              color: 'var(--text-faint)',
            }}
          >
            Agents read this at the start of every session, and it outlives the
            message history.
          </p>
        </div>
      </div>
    </div>
  )
}
